/**
 * ProductComparison - Modern Product Comparison Table Plugin
 *
 * Transforms a standard HTML table into a comparison table with:
 * - Sticky left column (labels) using CSS position: sticky
 * - Horizontally scrollable data columns
 * - No height synchronization needed (same table)
 * - Responsive design for desktop and mobile
 */

export class ProductComparison {
    // Konstanten für Magic Numbers
    static DEFAULTS = {
        IMAGE_LOAD_TIMEOUT: 5000,
        SCROLL_THRESHOLD: 10,
        DRAG_SCROLL_MULTIPLIER: 2,
        INDICATOR_UPDATE_DELAYS: [50, 200, 500],
        RESIZE_DEBOUNCE: 100,
        FALLBACK_ROW_HEIGHT: 40,
        FALLBACK_COL_COUNT: 5
    };

    constructor(target, options = {}) {
        // Options mit Defaults
        this.options = {
            marker: '###YES###',
            tickUrl: '/typo3conf/ext/avm/Resources/Public/Images/ico-checked.svg',
            tickImageAlt: 'Ja',
            ...options
        };

        // DOM Element (kein jQuery)
        this.target = target instanceof jQuery ? target[0] : target;

        if (!this.target || !this.target.tagName || this.target.tagName.toLowerCase() !== 'table') {
            console.warn('ProductComparison: Invalid target element');
            return;
        }

        // Referenzen
        this.wrapper = null;
        this.scrollContainer = null;
        this.placeholder = null;

        // Event-Listener und Timeout-Tracking (für Cleanup)
        this._eventListeners = [];
        this._timeouts = [];

        // Gecachte DOM-Queries
        this._rows = null;
        this._rowCount = 0;
        this._colCount = 0;

        // Initialisierung starten
        this.init();
    }

    /**
     * Haupt-Initialisierungsmethode
     */
    async init() {
        try {
            // 0. Cache DOM-Queries BEVOR Tabelle verändert wird
            // WICHTIG: Tabelle muss noch im DOM sein und sichtbar sein für korrekte Messung
            this._rows = this.target.querySelectorAll('tr');
            this._rowCount = this._rows.length;
            this._colCount = this._rows.length > 0 ? this._rows[0].querySelectorAll('td, th').length : ProductComparison.DEFAULTS.FALLBACK_COL_COUNT;

            // 1. Hole Zeilenhöhen BEVOR Tabelle verändert wird
            // Messe Höhe jeder Zeile (muss VOR _createWrapper() passieren!)
            const rowHeights = Array.from(this._rows).map(row => {
                // offsetHeight sollte funktionieren, auch wenn visibility: hidden
                // Falls nicht, nutze getBoundingClientRect als Fallback
                const height = row.offsetHeight;
                if (height > 0) {
                    return height;
                }
                // Fallback: getBoundingClientRect (funktioniert auch bei visibility: hidden)
                const rect = row.getBoundingClientRect();
                return rect.height > 0 ? rect.height : ProductComparison.DEFAULTS.FALLBACK_ROW_HEIGHT;
            });

            // 2. Erstelle Wrapper SO FRÜH WIE MÖGLICH (für CSS-Variablen)
            this._createWrapper();

            // 3. Zeige Placeholder mit gemessenen Zeilenhöhen
            this._showPlaceholder(rowHeights);

            // 4. Warte auf Bilder-Load
            await this._waitForImages();

            // 5. Baue DOM-Struktur um (Tabelle in Wrapper einfügen)
            this._buildStructure();

            // 6. Ersetze Marker (###YES### → Checkmark)
            this._replaceMarkers();

            // 7. Setup Scroll-Indikatoren
            this._setupScrollIndicators();

            // 8. Drag-to-Scroll für Desktop
            this._setupDragToScroll();

            // 9. Entferne Placeholder (Tabelle ist jetzt sichtbar)
            this._hidePlaceholder();
        } catch (error) {
            console.error('ProductComparison: Initialization error', error);
            // Bei Fehler auch Placeholder entfernen
            this._hidePlaceholder();
        }
    }

    /**
     * Erstellt den Wrapper so früh wie möglich (für CSS-Variablen)
     */
    _createWrapper() {
        const originalTable = this.target;
        const parentNode = originalTable.parentNode;

        // Haupt-Wrapper SO FRÜH WIE MÖGLICH erstellen
        const wrapper = document.createElement('div');
        wrapper.className = 'productcomparison';

        // Ersetze Tabelle durch Wrapper (Tabelle wird temporär entfernt)
        parentNode.replaceChild(wrapper, originalTable);

        // Speichere Referenz
        this.wrapper = wrapper;
    }

    /**
     * Zeigt einen Loading-Placeholder als Ersatz-Container an
     * @param {Array<number>} rowHeights - Array mit Höhen jeder Zeile
     */
    _showPlaceholder(rowHeights) {
        // Erstelle Placeholder als Ersatz-Container
        const placeholder = document.createElement('div');
        placeholder.className = 'productcomparison__placeholder';
        
        // Erstelle Skeleton-Container
        const skeleton = document.createElement('div');
        skeleton.className = 'productcomparison__placeholder-skeleton';

        // Erstelle Skeleton-Zeilen mit individuellen Höhen
        rowHeights.forEach((rowHeight, i) => {
            const row = document.createElement('div');
            row.className = 'productcomparison__placeholder-row';
            const height = rowHeight || ProductComparison.DEFAULTS.FALLBACK_ROW_HEIGHT;
            row.style.height = `${height}px`;
            row.style.minHeight = `${height}px`;

            // Erstelle exakt so viele Spalten wie in der Original-Tabelle
            for (let j = 0; j < this._colCount; j++) {
                const cell = document.createElement('div');
                cell.className = 'productcomparison__placeholder-cell';
                row.appendChild(cell);
            }

            skeleton.appendChild(row);
        });

        placeholder.appendChild(skeleton);

        // Füge Placeholder IN den Wrapper ein (als Ersatz für Tabelle)
        this.wrapper.appendChild(placeholder);

        this.placeholder = placeholder;
    }

    /**
     * Entfernt den Placeholder (Tabelle ist jetzt sichtbar)
     */
    _hidePlaceholder() {
        // Entferne Placeholder (Tabelle ist jetzt sichtbar)
        if (this.placeholder && this.placeholder.parentNode) {
            this.placeholder.parentNode.removeChild(this.placeholder);
            this.placeholder = null;
        }

        // Tabelle ist bereits sichtbar, keine weiteren Änderungen nötig
        if (this.target) {
            this.target.classList.add('productcomparison__table-visible');
        }
    }

    /**
     * Wartet auf das Laden aller Bilder in der Tabelle
     */
    async _waitForImages() {
        const images = this.target.querySelectorAll('img');
        if (images.length === 0) {
            return Promise.resolve();
        }

        const imagePromises = Array.from(images).map(img => {
            // Wenn Bild bereits geladen ist
            if (img.complete && img.naturalHeight !== 0) {
                return Promise.resolve();
            }

            // Warte auf Load oder Error
            return new Promise((resolve) => {
                const timeout = this._setTimeout(resolve, ProductComparison.DEFAULTS.IMAGE_LOAD_TIMEOUT);

                const loadHandler = () => {
                    clearTimeout(timeout);
                    resolve();
                };
                const errorHandler = () => {
                    clearTimeout(timeout);
                    resolve(); // Auch bei Fehler weitermachen
                };

                this._addEventListener(img, 'load', loadHandler, { once: true });
                this._addEventListener(img, 'error', errorHandler, { once: true });
            });
        });

        await Promise.all(imagePromises);
    }

    /**
     * Baut die neue DOM-Struktur auf
     * Die Tabelle wird in den bereits existierenden Wrapper eingefügt
     */
    _buildStructure() {
        const originalTable = this.target;

        // Scroll-Wrapper
        const scrollWrapper = document.createElement('div');
        scrollWrapper.className = 'productcomparison__scroll-wrapper';

        // Füge Tabelle zum Scroll-Wrapper hinzu
        scrollWrapper.appendChild(originalTable);

        // Füge Scroll-Wrapper in den bereits existierenden Wrapper ein
        // (Placeholder wird später entfernt)
        this.wrapper.appendChild(scrollWrapper);

        // Füge Klasse zur Tabelle hinzu (falls noch nicht vorhanden)
        if (!originalTable.classList.contains('ce-table-productcomparison')) {
            originalTable.classList.add('ce-table-productcomparison');
        }

        // Speichere Referenz
        this.scrollContainer = scrollWrapper;
    }

    /**
     * Ersetzt Marker (###YES###) durch Checkmark-Icons
     */
    _replaceMarkers() {
        const allCells = this.target.querySelectorAll('td, th');
        allCells.forEach(cell => {
            const text = cell.textContent.trim();
            if (text === this.options.marker) {
                cell.classList.add('td-tick');
                
                // XSS-Schutz: DOM-API statt innerHTML
                const img = document.createElement('img');
                img.src = this.options.tickUrl;
                img.alt = this.options.tickImageAlt;
                img.width = 26;
                img.height = 26;
                
                cell.innerHTML = '';
                cell.appendChild(img);
            }
        });
    }

    /**
     * Setup Scroll-Indikatoren (für visuelles Feedback)
     * Die Klassen werden auf dem Haupt-Container gesetzt, damit die Schatten fix bleiben
     */
    _setupScrollIndicators() {
        if (!this.scrollContainer || !this.wrapper) return;

        const updateIndicators = this._createIndicatorUpdater();
        this._setupIndicatorListeners(updateIndicators);
    }

    /**
     * Erstellt die Update-Funktion für Scroll-Indikatoren
     * @returns {Function} Update-Funktion für Indikatoren
     */
    _createIndicatorUpdater() {
        return () => {
            const scrollLeft = this.scrollContainer.scrollLeft;
            const scrollWidth = this.scrollContainer.scrollWidth;
            const clientWidth = this.scrollContainer.clientWidth;
            const maxScroll = scrollWidth - clientWidth;

            // Prüfe ob überhaupt gescrollt werden kann
            const canScroll = scrollWidth > clientWidth;

            // Füge/Entferne Klassen auf dem Haupt-Container für fixe Schatten
            const threshold = ProductComparison.DEFAULTS.SCROLL_THRESHOLD;
            this.wrapper.classList.toggle('can-scroll-left', canScroll && scrollLeft > threshold);
            this.wrapper.classList.toggle('can-scroll-right', canScroll && scrollLeft < maxScroll - threshold);
        };
    }

    /**
     * Setup Event-Listener für Scroll-Indikatoren
     * @param {Function} updateIndicators - Die Update-Funktion für Indikatoren
     */
    _setupIndicatorListeners(updateIndicators) {
        this._addEventListener(this.scrollContainer, 'scroll', updateIndicators, { passive: true });

        // Initiale Prüfung nach kurzer Verzögerung (damit Layout berechnet ist)
        ProductComparison.DEFAULTS.INDICATOR_UPDATE_DELAYS.forEach(delay => {
            this._setTimeout(updateIndicators, delay);
        });

        // Auch bei Window-Resize prüfen
        const resizeHandler = () => {
            this._setTimeout(updateIndicators, ProductComparison.DEFAULTS.RESIZE_DEBOUNCE);
        };
        this._addEventListener(window, 'resize', resizeHandler, { passive: true });
    }

    /**
     * Setup Drag-to-Scroll für horizontales Scrollen mit der Maus
     */
    _setupDragToScroll() {
        if (!this.scrollContainer) return;

        const dragState = {
            isDragging: false,
            startX: 0,
            scrollLeft: 0
        };

        const canScroll = this._canScroll();
        const updateCursor = this._createCursorUpdater(dragState, canScroll);
        const handlers = this._createDragHandlers(dragState, canScroll, updateCursor);

        // Event-Listener hinzufügen (mit Tracking)
        this._addEventListener(this.scrollContainer, 'mousedown', handlers.mouseDown);
        this._addEventListener(document, 'mousemove', handlers.mouseMove);
        this._addEventListener(document, 'mouseup', handlers.mouseUp);
        this._addEventListener(this.scrollContainer, 'mouseleave', handlers.mouseLeave);

        // Initiale Cursor-Prüfung
        updateCursor();

        // Update Cursor bei Resize (debounced)
        const debouncedCursorUpdate = this._debounce(updateCursor, ProductComparison.DEFAULTS.RESIZE_DEBOUNCE);
        this._addEventListener(window, 'resize', debouncedCursorUpdate, { passive: true });
    }

    /**
     * Prüft ob horizontales Scrollen möglich ist
     * @returns {Function} Funktion die prüft ob gescrollt werden kann
     */
    _canScroll() {
        return () => {
            return this.scrollContainer && 
                   this.scrollContainer.scrollWidth > this.scrollContainer.clientWidth;
        };
    }

    /**
     * Erstellt die Cursor-Update-Funktion
     * @param {Object} dragState - Der Drag-Zustand
     * @param {Function} canScroll - Funktion die prüft ob gescrollt werden kann
     * @returns {Function} Update-Funktion für Cursor
     */
    _createCursorUpdater(dragState, canScroll) {
        return () => {
            if (canScroll() && !dragState.isDragging) {
                this.scrollContainer.classList.add('can-drag-scroll');
            } else {
                this.scrollContainer.classList.remove('can-drag-scroll');
            }
        };
    }

    /**
     * Erstellt alle Drag-Event-Handler
     * @param {Object} dragState - Der Drag-Zustand
     * @param {Function} canScroll - Funktion die prüft ob gescrollt werden kann
     * @param {Function} updateCursor - Update-Funktion für Cursor
     * @returns {Object} Objekt mit allen Event-Handlern
     */
    _createDragHandlers(dragState, canScroll, updateCursor) {
        const handleMouseDown = (e) => {
            // Nur bei Linksklick
            if (e.button !== 0) return;

            // Nicht auf interaktive Elemente (Links, Buttons, Inputs)
            if (this._isInteractiveElement(e.target)) {
                return;
            }

            // Prüfe ob Scrollen möglich ist
            if (!canScroll()) return;

            dragState.isDragging = true;
            dragState.startX = e.pageX - this.scrollContainer.offsetLeft;
            dragState.scrollLeft = this.scrollContainer.scrollLeft;

            // Cursor ändern
            this.scrollContainer.style.cursor = 'grabbing';
            this.scrollContainer.style.userSelect = 'none';

            // Verhindere Text-Selektion und Standard-Verhalten
            e.preventDefault();
            e.stopPropagation();

            // Füge Klasse für CSS-Styling hinzu
            this.scrollContainer.classList.add('is-dragging');
        };

        const handleMouseMove = (e) => {
            if (!dragState.isDragging) return;

            e.preventDefault();

            const x = e.pageX - this.scrollContainer.offsetLeft;
            const walk = (x - dragState.startX) * ProductComparison.DEFAULTS.DRAG_SCROLL_MULTIPLIER;
            this.scrollContainer.scrollLeft = dragState.scrollLeft - walk;
        };

        const handleMouseUp = () => {
            if (!dragState.isDragging) return;

            dragState.isDragging = false;
            this.scrollContainer.style.cursor = '';
            this.scrollContainer.style.userSelect = '';
            this.scrollContainer.classList.remove('is-dragging');
            updateCursor();
        };

        const handleMouseLeave = () => {
            // Wenn Maus den Container verlässt, beende Drag
            if (dragState.isDragging) {
                handleMouseUp();
            }
        };

        return {
            mouseDown: handleMouseDown,
            mouseMove: handleMouseMove,
            mouseUp: handleMouseUp,
            mouseLeave: handleMouseLeave
        };
    }

    /**
     * Prüft ob ein Element interaktiv ist (Link, Button, Input)
     * @param {Element} element - Das zu prüfende Element
     * @returns {boolean} true wenn Element interaktiv ist
     */
    _isInteractiveElement(element) {
        const tagName = element.tagName;
        return tagName === 'A' || 
               tagName === 'BUTTON' || 
               tagName === 'INPUT' || 
               element.closest('a, button, input') !== null;
    }

    /**
     * Fügt einen Event-Listener hinzu und trackt ihn für späteres Cleanup
     * @param {Element|Window|Document} element - Das Element, auf dem der Listener registriert wird
     * @param {string} event - Der Event-Name
     * @param {Function} handler - Der Event-Handler
     * @param {Object} options - Event-Options (z.B. { passive: true, once: true })
     */
    _addEventListener(element, event, handler, options = {}) {
        element.addEventListener(event, handler, options);
        this._eventListeners.push({ element, event, handler, options });
    }

    /**
     * Erstellt einen Timeout und trackt ihn für späteres Cleanup
     * @param {Function} callback - Die Callback-Funktion
     * @param {number} delay - Verzögerung in Millisekunden
     * @returns {number} Timeout-ID
     */
    _setTimeout(callback, delay) {
        const timeout = setTimeout(callback, delay);
        this._timeouts.push(timeout);
        return timeout;
    }

    /**
     * Debounce-Helper für Performance-Optimierung
     */
    _debounce(func, wait) {
        let timeout;
        return (...args) => {
            const later = () => {
                if (timeout) {
                    // Entferne Timeout aus Tracking, wenn es noch existiert
                    const index = this._timeouts.indexOf(timeout);
                    if (index > -1) {
                        this._timeouts.splice(index, 1);
                    }
                    clearTimeout(timeout);
                }
                func(...args);
            };
            if (timeout) {
                clearTimeout(timeout);
                const index = this._timeouts.indexOf(timeout);
                if (index > -1) {
                    this._timeouts.splice(index, 1);
                }
            }
            timeout = this._setTimeout(later, wait);
        };
    }

    /**
     * Cleanup-Methode (für zukünftige Verwendung, z.B. bei dynamischen Updates)
     * Entfernt alle Event-Listener und Timeouts, um Memory Leaks zu vermeiden
     */
    destroy() {
        // Entferne alle Event-Listener
        if (this._eventListeners) {
            this._eventListeners.forEach(({ element, event, handler, options }) => {
                element.removeEventListener(event, handler, options);
            });
            this._eventListeners = [];
        }

        // Entferne alle Timeouts
        if (this._timeouts) {
            this._timeouts.forEach(timeout => clearTimeout(timeout));
            this._timeouts = [];
        }

        // Entferne Placeholder falls noch vorhanden
        this._hidePlaceholder();

        // Reset Referenzen
        this.wrapper = null;
        this.scrollContainer = null;
        this.placeholder = null;
        this._rows = null;
        this._rowCount = 0;
        this._colCount = 0;
    }
}
